import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  addYears,
  subYears,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  getMonth,
  getYear,
  setMonth as dfSetMonth,
  setYear as dfSetYear,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  AlertTriangle,
  ArrowLeftRight,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  FilterX,
  LayoutGrid,
  Pencil,
  Trash2,
  X,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import {
  FINANCIAL_WIDGET_IDS,
  GRID_COLS,
  GRID_ROWS,
  validateNoOverlap,
  type WidgetLayout,
} from '@proadv/types';

import {
  apiClient,
  type ApiError,
  type FinancialTransactionDeleteScope,
  type FinancialTransactionListItem,
  type FinancialTransactionStatus,
  type FinancialTransactionType,
  type ListTransactionsParams,
  type FinancialTransactionsTotalsResponse,
} from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { GlassDateRangePicker } from '@/components/ui/glass-date-range-picker';
import { GlassSelect } from '@/components/ui/glass-select';
import { AnchoredPopover } from '@/components/ui/anchored-popover';
import { CategoryFilter } from '@/components/financial/category-filter';
import { AddWidgetMenu } from '@/components/dashboard/add-widget-menu';
import { DashboardGrid } from '@/components/dashboard/dashboard-grid';
import { FINANCIAL_DEFAULT_LAYOUT } from '@/components/dashboard/financial-widgets/default-layout';

const KNOWN = new Set<string>(FINANCIAL_WIDGET_IDS);

const PAGE_SIZE_KEY = 'proadv.financial.pageSize';
const PAGE_SIZE_OPTIONS = [50, 100, 200, 500, 1000, 2000] as const;

function readStoredPageSize(): number {
  try {
    const v = localStorage.getItem(PAGE_SIZE_KEY);
    if (v) {
      const n = Number(v);
      if (PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])) {
        return n;
      }
    }
  } catch {
    /* ignora erro de storage */
  }
  return 50;
}

const DATE_DIMENSION_KEY = 'proadv.financial.dateDimension';
type DateDimension = 'due' | 'payment';

function readStoredDimension(): DateDimension {
  try {
    const v = localStorage.getItem(DATE_DIMENSION_KEY);
    if (v === 'due' || v === 'payment') return v;
  } catch {
    /* ignora */
  }
  return 'due';
}

// Mes atual (mesma regra do botao "Mes atual" do GlassDateRangePicker).
function currentMonthRange(): { from: string; to: string } {
  const now = new Date();
  return {
    from: format(startOfMonth(now), 'yyyy-MM-dd'),
    to: format(endOfMonth(now), 'yyyy-MM-dd'),
  };
}

// Persistencia dos filtros da toolbar (busca, tipo, status, conta, periodo,
// ordenacao) para sobreviver a sair/voltar da rota. clientId/caseId/contractId
// ficam de fora de proposito: nao tem controle na toolbar, sao deep-link de
// outra tela e nao devem "grudar" de forma invisivel entre visitas.
const FILTERS_KEY = 'proadv.financial.filters.v1';

interface StoredFinancialFilters {
  search?: string;
  type?: FinancialTransactionType;
  status?: FinancialTransactionStatus;
  accountId?: string;
  categoryIds?: string[];
  dateDimension?: DateDimension;
  dateFrom?: string;
  dateTo?: string;
  orderBy?: NonNullable<ListTransactionsParams['orderBy']>;
  orderDir?: NonNullable<ListTransactionsParams['orderDir']>;
}

const ORDER_BY_VALUES = new Set<NonNullable<ListTransactionsParams['orderBy']>>([
  'dueDate',
  'paymentDate',
  'createdAt',
  'amount',
  'category',
]);
const STATUS_VALUES = new Set<FinancialTransactionStatus>([
  'pending',
  'paid',
  'overdue',
  'canceled',
]);
const TYPE_VALUES = new Set<FinancialTransactionType>(['income', 'expense', 'transfer']);

function readStoredFilters(): StoredFinancialFilters | null {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return null;
    const out: StoredFinancialFilters = {};
    if (typeof parsed.search === 'string') out.search = parsed.search;
    if (typeof parsed.type === 'string' && TYPE_VALUES.has(parsed.type as FinancialTransactionType)) {
      out.type = parsed.type as FinancialTransactionType;
    }
    if (
      typeof parsed.status === 'string' &&
      STATUS_VALUES.has(parsed.status as FinancialTransactionStatus)
    ) {
      out.status = parsed.status as FinancialTransactionStatus;
    }
    if (typeof parsed.accountId === 'string') out.accountId = parsed.accountId;
    if (Array.isArray(parsed.categoryIds)) {
      const ids = parsed.categoryIds.filter((v): v is string => typeof v === 'string');
      if (ids.length > 0) out.categoryIds = ids;
    }
    if (parsed.dateDimension === 'due' || parsed.dateDimension === 'payment') {
      out.dateDimension = parsed.dateDimension;
    }
    if (typeof parsed.dateFrom === 'string') out.dateFrom = parsed.dateFrom;
    if (typeof parsed.dateTo === 'string') out.dateTo = parsed.dateTo;
    if (
      typeof parsed.orderBy === 'string' &&
      ORDER_BY_VALUES.has(parsed.orderBy as NonNullable<ListTransactionsParams['orderBy']>)
    ) {
      out.orderBy = parsed.orderBy as StoredFinancialFilters['orderBy'];
    }
    if (parsed.orderDir === 'asc' || parsed.orderDir === 'desc') {
      out.orderDir = parsed.orderDir;
    }
    return out;
  } catch {
    return null;
  }
}

// Range de data salvo (from+to sempre juntos; nunca mistura um salvo com um default).
function resolveStoredRange(stored: StoredFinancialFilters | null): { from: string; to: string } {
  if (stored?.dateFrom && stored?.dateTo) {
    return { from: stored.dateFrom, to: stored.dateTo };
  }
  return currentMonthRange();
}

const DATE_DIMENSION_OPTIONS = [
  { value: 'due', label: 'Vencimento' },
  { value: 'payment', label: 'Pagamento' },
] as const;

const STATUS_LABEL: Record<FinancialTransactionStatus, string> = {
  pending: 'Pendente',
  paid: 'Pago',
  overdue: 'Atrasado',
  canceled: 'Cancelado',
};

const TYPE_LABEL: Record<FinancialTransactionType, string> = {
  income: 'Receita',
  expense: 'Despesa',
  transfer: 'Transferência',
};

function formatBrl(n: number): string {
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

function formatBrlString(s: string | number): string {
  const n = typeof s === 'string' ? Number(s) : s;
  if (Number.isNaN(n)) return 'R$ 0,00';
  return formatBrl(n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const onlyDate = iso.length === 10 ? `${iso}T12:00:00Z` : iso;
  const d = new Date(onlyDate);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

// ── Mini-calendário single-date usado no popover de edição de data de pagamento ──

function parseIsoDate(v: string): Date | null {
  if (!v) return null;
  try {
    const d = parseISO(v);
    if (Number.isNaN(d.getTime())) return null;
    return d;
  } catch {
    return null;
  }
}

function toIsoDate(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function calcYearGroupStart(year: number): number {
  return Math.floor((year - 1) / 12) * 12 + 1;
}

function buildMonthDays(month: Date): Date[] {
  const start = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
  const end = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
  const days: Date[] = [];
  let cur = start;
  while (cur <= end) {
    days.push(cur);
    cur = addDays(cur, 1);
  }
  return days;
}

const MINI_CAL_WEEK_LABELS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const MINI_CAL_MONTH_LABELS = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];
const MINI_CAL_CELL_BASE =
  'flex items-center justify-center rounded-md text-xs font-medium transition cursor-pointer select-none h-8';
const MINI_CAL_CELL_DEFAULT =
  'text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]';
const MINI_CAL_CELL_SELECTED = 'bg-[var(--accent)] text-white hover:opacity-90';
const MINI_CAL_CELL_TODAY = 'ring-1 ring-[var(--accent)] text-[var(--accent)]';

/**
 * Corpo de calendário (dias/meses/anos) sem trigger proprio — hospedado
 * dentro de um AnchoredPopover. Existe pq GlassDatePicker tem seu proprio
 * dropdown `absolute` (nao-portal) que seria cortado pelo `overflow-hidden`
 * do container da tabela; o mesmo padrao (mini-calendario + AnchoredPopover)
 * ja e usado em glass-date-range-picker.tsx.
 */
function MiniPaymentCalendar({
  value,
  onSelect,
}: {
  value: string;
  onSelect: (iso: string) => void;
}) {
  const [view, setView] = useState<'days' | 'months' | 'years'>('days');
  const [month, setMonth] = useState<Date>(() => parseIsoDate(value) ?? new Date());
  const [ygStart, setYgStart] = useState<number>(() =>
    calcYearGroupStart(getYear(parseIsoDate(value) ?? new Date())),
  );

  const selected = parseIsoDate(value);
  const days = buildMonthDays(month);
  const currentYear = getYear(month);
  const yearGroup = Array.from({ length: 12 }, (_, i) => ygStart + i);
  const nowYear = getYear(new Date());
  const nowMonth = getMonth(new Date());

  function handlePrev() {
    if (view === 'days') setMonth((m) => subMonths(m, 1));
    else if (view === 'months') setMonth((m) => subYears(m, 1));
    else setYgStart((s) => s - 12);
  }

  function handleNext() {
    if (view === 'days') setMonth((m) => addMonths(m, 1));
    else if (view === 'months') setMonth((m) => addYears(m, 1));
    else setYgStart((s) => s + 12);
  }

  function selectMonth(idx: number) {
    setMonth((m) => dfSetMonth(m, idx));
    setView('days');
  }

  function selectYear(year: number) {
    setMonth((m) => dfSetYear(m, year));
    setYgStart(calcYearGroupStart(year));
    setView('months');
  }

  function goToday() {
    const today = new Date();
    setMonth(today);
    setYgStart(calcYearGroupStart(getYear(today)));
    setView('days');
    onSelect(toIsoDate(today));
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-[var(--popover-border,var(--card-border))]">
        <button
          type="button"
          onClick={handlePrev}
          className="flex h-6 w-6 items-center justify-center rounded hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition"
          aria-label="Anterior"
        >
          <ChevronLeft className="h-3.5 w-3.5 text-[var(--fg)]" />
        </button>

        {view === 'days' && (
          <div className="flex items-center gap-0.5 text-xs font-semibold text-[var(--fg)]">
            <button
              type="button"
              onClick={() => setView('months')}
              className="rounded px-1 py-0.5 hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition capitalize"
            >
              {format(month, 'MMMM', { locale: ptBR })}
            </button>
            <button
              type="button"
              onClick={() => setView('years')}
              className="rounded px-1 py-0.5 hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition"
            >
              {currentYear}
            </button>
          </div>
        )}

        {view === 'months' && (
          <button
            type="button"
            onClick={() => setView('years')}
            className="text-xs font-semibold text-[var(--fg)] rounded px-1 py-0.5 hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition"
          >
            {currentYear}
          </button>
        )}

        {view === 'years' && (
          <span className="text-xs font-semibold text-[var(--fg)] select-none">
            {ygStart} – {ygStart + 11}
          </span>
        )}

        <button
          type="button"
          onClick={handleNext}
          className="flex h-6 w-6 items-center justify-center rounded hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition"
          aria-label="Próximo"
        >
          <ChevronRight className="h-3.5 w-3.5 text-[var(--fg)]" />
        </button>
      </div>

      {view === 'days' && (
        <>
          <div className="grid grid-cols-7 px-1.5 pt-1.5">
            {MINI_CAL_WEEK_LABELS.map((l, i) => (
              <div
                key={i}
                className="flex h-6 items-center justify-center text-[10px] font-medium text-[var(--fg-muted)]"
              >
                {l}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 px-1.5 pb-1.5">
            {days.map((day, idx) => {
              const iso = toIsoDate(day);
              const isCurrentMonth = isSameMonth(day, month);
              const isSelectedDay = selected ? isSameDay(day, selected) : false;
              const isTodayDay = isToday(day);

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => onSelect(iso)}
                  className={cn(
                    'flex h-7 w-full items-center justify-center rounded-md text-xs transition',
                    !isCurrentMonth && 'text-[var(--fg-muted)] opacity-40',
                    isCurrentMonth && !isSelectedDay && !isTodayDay &&
                      'text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]',
                    isTodayDay && !isSelectedDay &&
                      'ring-1 ring-[var(--accent)] text-[var(--accent)]',
                    isSelectedDay &&
                      'bg-[var(--accent)] text-white font-medium hover:opacity-90',
                  )}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>
        </>
      )}

      {view === 'months' && (
        <div className="grid grid-cols-4 gap-1 px-2 py-2">
          {MINI_CAL_MONTH_LABELS.map((label, idx) => {
            const isSelectedMonth = selected
              ? getYear(selected) === currentYear && getMonth(selected) === idx
              : false;
            const isTodayMonth = nowYear === currentYear && nowMonth === idx;

            return (
              <button
                key={idx}
                type="button"
                onClick={() => selectMonth(idx)}
                className={cn(
                  MINI_CAL_CELL_BASE,
                  isSelectedMonth
                    ? MINI_CAL_CELL_SELECTED
                    : isTodayMonth
                      ? MINI_CAL_CELL_TODAY
                      : MINI_CAL_CELL_DEFAULT,
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {view === 'years' && (
        <div className="grid grid-cols-4 gap-1 px-2 py-2">
          {yearGroup.map((yr) => {
            const isSelectedYear = selected ? getYear(selected) === yr : false;
            const isTodayYear = nowYear === yr;

            return (
              <button
                key={yr}
                type="button"
                onClick={() => selectYear(yr)}
                className={cn(
                  MINI_CAL_CELL_BASE,
                  isSelectedYear
                    ? MINI_CAL_CELL_SELECTED
                    : isTodayYear
                      ? MINI_CAL_CELL_TODAY
                      : MINI_CAL_CELL_DEFAULT,
                )}
              >
                {yr}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-end border-t border-[var(--popover-border,var(--card-border))] px-3 py-1.5">
        <button
          type="button"
          onClick={goToday}
          className="text-[11px] text-[var(--accent)] hover:opacity-80 transition"
        >
          Hoje
        </button>
      </div>
    </div>
  );
}

/**
 * Celula "Pagamento" da tabela de lancamentos. So-leitura quando o
 * lancamento nao esta pago (comportamento inalterado). Quando pago, vira
 * um trigger clicavel que abre um calendario single num popover ancorado
 * pra corrigir a data de recebimento/pagamento sem precisar entrar no
 * lancamento.
 */
function PaymentDateCell({
  tx,
  onEditPaymentDate,
}: {
  tx: FinancialTransactionListItem;
  onEditPaymentDate: (id: string, isoDate: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasError, setHasError] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  if (tx.status !== 'paid') {
    return (
      <td className="px-3 py-2 tabular-nums text-[var(--fg-muted)]">
        {formatDate(tx.paymentDate)}
      </td>
    );
  }

  async function handleSelect(iso: string) {
    setSaving(true);
    setHasError(false);
    try {
      await onEditPaymentDate(tx.id, iso);
      setOpen(false);
    } catch (err) {
      console.error('Falha ao atualizar data de pagamento', err);
      setHasError(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <td className="px-3 py-2 tabular-nums text-[var(--fg-muted)]">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !saving && setOpen((v) => !v)}
        disabled={saving}
        className={cn(
          'group inline-flex items-center gap-1 rounded px-1 -mx-1 transition',
          'hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:underline underline-offset-2',
          saving && 'opacity-50 cursor-wait',
          hasError && 'text-red-600',
        )}
        title={
          tx.type === 'income'
            ? 'Clique para alterar a data de recebimento'
            : 'Clique para alterar a data de pagamento'
        }
      >
        {formatDate(tx.paymentDate)}
        <Pencil className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-60" />
      </button>

      <AnchoredPopover
        anchorRef={triggerRef}
        open={open}
        onClose={() => !saving && setOpen(false)}
        width={240}
        align="start"
      >
        <div className={cn(saving && 'pointer-events-none opacity-60')}>
          <MiniPaymentCalendar value={tx.paymentDate ?? ''} onSelect={handleSelect} />
        </div>
      </AnchoredPopover>
    </td>
  );
}

function sanitizeLayout(
  input: WidgetLayout[] | null | undefined,
): WidgetLayout[] {
  if (!input || input.length === 0) return FINANCIAL_DEFAULT_LAYOUT;
  const out = input
    .filter((w) => KNOWN.has(w.widgetId))
    .map((w) => ({
      widgetId: w.widgetId,
      x: Math.max(0, Math.min(GRID_COLS - 1, Math.floor(w.x))),
      y: Math.max(0, Math.min(GRID_ROWS - 1, Math.floor(w.y))),
      w: Math.max(1, Math.min(4, Math.floor(w.w))),
      h: Math.max(1, Math.min(3, Math.floor(w.h))),
    }));
  return out.length > 0 ? out : FINANCIAL_DEFAULT_LAYOUT;
}

function findFreeSpot(layout: WidgetLayout[]): { x: number; y: number } {
  for (let y = 0; y < GRID_ROWS; y++) {
    for (let x = 0; x < GRID_COLS; x++) {
      const tentative: WidgetLayout = {
        widgetId: '__probe__',
        x,
        y,
        w: 1,
        h: 1,
      };
      const conflict = validateNoOverlap([...layout, tentative]);
      if (!conflict) return { x, y };
    }
  }
  return { x: 0, y: GRID_ROWS - 1 };
}

interface SortableThProps {
  field: NonNullable<ListTransactionsParams['orderBy']>;
  currentOrderBy: ListTransactionsParams['orderBy'];
  currentOrderDir: ListTransactionsParams['orderDir'];
  onSort: (field: NonNullable<ListTransactionsParams['orderBy']>) => void;
  className?: string;
  align?: 'left' | 'right';
  children: React.ReactNode;
}

function SortableTh({
  field,
  currentOrderBy,
  currentOrderDir,
  onSort,
  className,
  align = 'left',
  children,
}: SortableThProps) {
  const active = currentOrderBy === field;
  const arrow = !active ? '' : currentOrderDir === 'asc' ? ' ↑' : ' ↓';
  return (
    <th className={className}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={cn(
          'inline-flex items-center gap-0.5 cursor-pointer select-none transition-colors uppercase tracking-wide text-xs',
          active ? 'text-[var(--fg)]' : 'text-[var(--fg-muted)] hover:text-[var(--fg)]',
          align === 'right' && 'justify-end w-full',
        )}
      >
        {children}
        <span className={cn('opacity-70', !active && 'opacity-0')}>{arrow || ' ↓'}</span>
      </button>
    </th>
  );
}

export default function FinanceiroPage() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [draftLayout, setDraftLayout] = useState<WidgetLayout[] | null>(null);

  // Filtros da lista — separados do dashboard.
  // Precedencia: querystring (deep-link de outra tela) > filtro salvo em
  // localStorage (sessao anterior) > default (mes atual).
  const initialStatus = (searchParams.get('status') as
    | FinancialTransactionStatus
    | null) ?? undefined;
  const initialClientId = searchParams.get('clientId') ?? undefined;
  const initialCaseId = searchParams.get('caseId') ?? undefined;
  const initialContractId = searchParams.get('contractId') ?? undefined;
  const [filters, setFilters] = useState<ListTransactionsParams>(() => {
    const stored = readStoredFilters();
    const dimension = stored?.dateDimension ?? readStoredDimension();
    const { from, to } = resolveStoredRange(stored);
    return {
      page: 1,
      pageSize: readStoredPageSize(),
      orderBy: stored?.orderBy ?? 'dueDate',
      orderDir: stored?.orderDir ?? 'desc',
      type: stored?.type,
      status: initialStatus ?? stored?.status,
      accountId: stored?.accountId,
      categoryIds: stored?.categoryIds,
      clientId: initialClientId,
      caseId: initialCaseId,
      contractId: initialContractId,
      dueFrom: dimension === 'due' ? from : undefined,
      dueTo: dimension === 'due' ? to : undefined,
      paymentFrom: dimension === 'payment' ? from : undefined,
      paymentTo: dimension === 'payment' ? to : undefined,
    };
  });
  const [search, setSearch] = useState<string>(() => readStoredFilters()?.search ?? '');
  const pageInputRef = useRef<HTMLInputElement>(null);
  // Quando handleClearFilters roda, pula a proxima gravacao automatica (senao
  // o useEffect de persistir recria a chave que acabou de ser removida).
  const skipNextPersistRef = useRef(false);

  // Filtros de data — abre com o mes atual, ou com o que ficou salvo da ultima visita.
  const [dateDimension, setDateDimension] = useState<DateDimension>(
    () => readStoredFilters()?.dateDimension ?? readStoredDimension(),
  );
  const [dateFrom, setDateFrom] = useState<string>(
    () => resolveStoredRange(readStoredFilters()).from,
  );
  const [dateTo, setDateTo] = useState<string>(
    () => resolveStoredRange(readStoredFilters()).to,
  );

  // Layout queries
  const layoutQuery = useQuery({
    queryKey: ['me', 'dashboard-layout', 'financial'],
    queryFn: () => apiClient.meGetDashboardLayout('financial'),
    staleTime: 1000 * 60,
  });

  const savedLayout = useMemo(
    () => sanitizeLayout(layoutQuery.data?.layout),
    [layoutQuery.data],
  );

  const activeLayout =
    mode === 'edit' && draftLayout ? draftLayout : savedLayout;

  const saveMutation = useMutation({
    mutationFn: (layout: WidgetLayout[]) =>
      apiClient.meUpdateDashboardLayout('financial', layout),
    onSuccess: (res) => {
      queryClient.setQueryData(['me', 'dashboard-layout', 'financial'], {
        layout: res.layout,
      });
      setMode('view');
      setDraftLayout(null);
    },
  });

  function enterEditMode() {
    setDraftLayout(savedLayout.map((w) => ({ ...w })));
    setMode('edit');
  }

  function cancelEdit() {
    setMode('view');
    setDraftLayout(null);
    saveMutation.reset();
  }

  function saveEdit() {
    if (!draftLayout) return;
    const overlap = validateNoOverlap(draftLayout);
    if (overlap) return;
    saveMutation.mutate(draftLayout);
  }

  function handleLayoutChange(next: WidgetLayout[]) {
    if (mode !== 'edit') return;
    setDraftLayout(next);
  }

  function handleRemove(widgetId: string) {
    if (mode !== 'edit') return;
    setDraftLayout((prev) =>
      (prev ?? savedLayout).filter((w) => w.widgetId !== widgetId),
    );
  }

  function handleAdd(widgetId: string) {
    setDraftLayout((prev) => {
      const base = prev ?? savedLayout;
      if (base.some((w) => w.widgetId === widgetId)) return base;
      const spot = findFreeSpot(base);
      return [...base, { widgetId, x: spot.x, y: spot.y, w: 1, h: 1 }];
    });
  }

  function applyDateFilters(
    from: string,
    to: string,
    dimension: DateDimension,
  ) {
    setFilters((f) => ({
      ...f,
      page: 1,
      dueFrom: dimension === 'due' ? (from || undefined) : undefined,
      dueTo: dimension === 'due' ? (to || undefined) : undefined,
      paymentFrom: dimension === 'payment' ? (from || undefined) : undefined,
      paymentTo: dimension === 'payment' ? (to || undefined) : undefined,
    }));
  }

  function handleDimensionChange(dim: string) {
    const next = dim as DateDimension;
    try { localStorage.setItem(DATE_DIMENSION_KEY, next); } catch { /* ignora */ }
    setDateDimension(next);
    applyDateFilters(dateFrom, dateTo, next);
  }

  function handleDateRangeChange({ from, to }: { from: string; to: string }) {
    setDateFrom(from);
    setDateTo(to);
    applyDateFilters(from, to, dateDimension);
  }

  // Recalculado a cada render de proposito (nao usa useMemo): precisa refletir
  // o mes atual mesmo se a aba ficar aberta atravessando a virada do mes.
  const defaultRange = currentMonthRange();

  const hasActiveFilters =
    search !== '' ||
    !!filters.type ||
    !!filters.status ||
    !!filters.accountId ||
    (filters.categoryIds?.length ?? 0) > 0 ||
    !!filters.clientId ||
    !!filters.caseId ||
    !!filters.contractId ||
    dateDimension !== 'due' ||
    dateFrom !== defaultRange.from ||
    dateTo !== defaultRange.to ||
    filters.orderBy !== 'dueDate' ||
    filters.orderDir !== 'desc';

  function handleClearFilters() {
    const { from, to } = currentMonthRange();
    skipNextPersistRef.current = true;
    setSearch('');
    setDateDimension('due');
    setDateFrom(from);
    setDateTo(to);
    setFilters((f) => ({
      ...f,
      page: 1,
      type: undefined,
      status: undefined,
      accountId: undefined,
      categoryIds: undefined,
      clientId: undefined,
      caseId: undefined,
      contractId: undefined,
      orderBy: 'dueDate',
      orderDir: 'desc',
      dueFrom: from,
      dueTo: to,
      paymentFrom: undefined,
      paymentTo: undefined,
    }));
    try {
      localStorage.setItem(DATE_DIMENSION_KEY, 'due');
      localStorage.removeItem(FILTERS_KEY);
    } catch {
      /* ignora erro de storage */
    }
  }

  useEffect(() => {
    if (mode !== 'edit') return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
      } else if (
        (e.key === 's' || e.key === 'S') &&
        (e.ctrlKey || e.metaKey)
      ) {
        e.preventDefault();
        saveEdit();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, draftLayout]);

  // Resetar page quando search muda
  useEffect(() => {
    setFilters((f) => ({ ...f, page: 1 }));
  }, [search]);

  // Persiste os filtros da toolbar para sobreviver a sair/voltar da rota
  // (pageSize ja tem chave propria e continua sendo salvo em changePageSize).
  useEffect(() => {
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }
    try {
      const snapshot: StoredFinancialFilters = {
        search: search || undefined,
        type: filters.type,
        status: filters.status,
        accountId: filters.accountId,
        categoryIds: filters.categoryIds,
        dateDimension,
        dateFrom,
        dateTo,
        orderBy: filters.orderBy,
        orderDir: filters.orderDir,
      };
      localStorage.setItem(FILTERS_KEY, JSON.stringify(snapshot));
    } catch {
      /* ignora erro de storage */
    }
  }, [
    search,
    filters.type,
    filters.status,
    filters.accountId,
    filters.categoryIds,
    dateDimension,
    dateFrom,
    dateTo,
    filters.orderBy,
    filters.orderDir,
  ]);

  // Lista
  const listQ = useQuery({
    queryKey: ['financial', 'tx', 'list', filters, search],
    queryFn: () =>
      apiClient.financialTransactionsList({
        ...filters,
        search: search || undefined,
      }),
  });

  // Helpers de paginação
  const currentPage = filters.page ?? 1;
  const currentPageSize = filters.pageSize ?? 50;
  const totalItems = listQ.data?.total ?? 0;
  const totalPages = listQ.data?.totalPages ?? 1;

  function goToPage(p: number) {
    const next = Math.max(1, Math.min(p, totalPages));
    setFilters((f) => ({ ...f, page: next }));
  }

  function changePageSize(size: number) {
    try {
      localStorage.setItem(PAGE_SIZE_KEY, String(size));
    } catch {
      /* ignora */
    }
    setFilters((f) => ({ ...f, pageSize: size, page: 1 }));
  }

  function handleSort(field: NonNullable<ListTransactionsParams['orderBy']>) {
    setFilters((f) => {
      const sameField = f.orderBy === field;
      const nextDir: 'asc' | 'desc' = sameField
        ? f.orderDir === 'asc' ? 'desc' : 'asc'
        : field === 'amount' || field === 'category' ? 'asc' : 'desc';
      return { ...f, orderBy: field, orderDir: nextDir, page: 1 };
    });
  }

  const firstItem = totalItems === 0 ? 0 : (currentPage - 1) * currentPageSize + 1;
  const lastItem = Math.min(currentPage * currentPageSize, totalItems);

  // Totais do filtro inteiro (sem page/pageSize — reflete todos os registros do filtro)
  const totalsFilters = {
    type: filters.type,
    status: filters.status,
    accountId: filters.accountId,
    categoryId: filters.categoryId,
    categoryIds: filters.categoryIds,
    costCenterId: filters.costCenterId,
    clientId: filters.clientId,
    caseId: filters.caseId,
    contractId: filters.contractId,
    dueFrom: filters.dueFrom,
    dueTo: filters.dueTo,
    paymentFrom: filters.paymentFrom,
    paymentTo: filters.paymentTo,
    search: search || undefined,
  };
  const totalsQ = useQuery({
    queryKey: ['financial', 'tx', 'totals', totalsFilters],
    queryFn: () => apiClient.financialTransactionsTotals(totalsFilters),
  });

  const accountsQ = useQuery({
    queryKey: ['financial', 'accounts'],
    queryFn: () => apiClient.bankAccountsList(),
  });

  const markPaidMut = useMutation({
    mutationFn: (id: string) => apiClient.financialTransactionsMarkPaid(id, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['financial'] });
    },
  });

  const markUnpaidMut = useMutation({
    mutationFn: (id: string) => apiClient.financialTransactionsMarkUnpaid(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['financial'] });
    },
  });

  const updatePaymentDateMut = useMutation({
    mutationFn: ({ id, paymentDate }: { id: string; paymentDate: string }) =>
      apiClient.financialTransactionsUpdate(id, { paymentDate }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['financial'] });
    },
  });

  // Exclusao (lixeira na lista). 'apenas este' reusa o DELETE simples ja
  // existente; escopo com serie (this_and_next/all) usa o endpoint novo de
  // lote. As duas mutations fecham o dialog e invalidam a lista/totais.
  const [deleteTarget, setDeleteTarget] = useState<FinancialTransactionListItem | null>(null);

  const deleteSimpleMut = useMutation({
    mutationFn: (id: string) => apiClient.financialTransactionsDelete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['financial'] });
      setDeleteTarget(null);
    },
  });

  const deleteBatchMut = useMutation({
    mutationFn: ({ id, scope }: { id: string; scope: FinancialTransactionDeleteScope }) =>
      apiClient.financialTransactionsDeleteBatch(id, scope),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['financial'] });
      setDeleteTarget(null);
    },
  });

  const items = listQ.data?.items ?? [];

  const totals: FinancialTransactionsTotalsResponse | undefined = totalsQ.data;

  const presentIds = activeLayout.map((w) => w.widgetId);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <div data-tour="financeiro-novo-lancamento" className="flex flex-wrap items-center gap-2">
          <Link
            to="/escritorio/financeiro/novo?type=income"
            className={cn(
              'inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition',
              'bg-emerald-600 text-white hover:opacity-90',
            )}
          >
            <TrendingUp className="h-3.5 w-3.5" />
            Nova receita
          </Link>
          <Link
            to="/escritorio/financeiro/novo?type=expense"
            className={cn(
              'inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition',
              'bg-red-600 text-white hover:opacity-90',
            )}
          >
            <TrendingDown className="h-3.5 w-3.5" />
            Nova despesa
          </Link>
          <Link
            to="/escritorio/financeiro/novo?type=transfer"
            className={cn(
              'inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-xs font-medium transition',
              'border-[var(--border)] bg-[var(--surface)] text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]',
            )}
          >
            <ArrowLeftRight className="h-3.5 w-3.5" />
            Transferência
          </Link>
          </div>
          <button
            type="button"
            onClick={() => (mode === 'edit' ? cancelEdit() : enterEditMode())}
            aria-label={
              mode === 'edit' ? 'Sair do modo edição' : 'Editar dashboard'
            }
            title={
              mode === 'edit' ? 'Sair do modo edição' : 'Editar dashboard'
            }
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded-md border transition',
              'border-[var(--border)] bg-[var(--surface)] text-[var(--fg-muted)] hover:text-[var(--fg)]',
              mode === 'edit' && 'text-[var(--accent)]',
            )}
          >
            {mode === 'edit' ? (
              <X className="h-4 w-4" />
            ) : (
              <LayoutGrid className="h-4 w-4" />
            )}
          </button>
        </div>
      </header>

      {mode === 'edit' && (
        <div
          className={cn(
            'flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2',
            'border-[var(--accent)]/40 bg-[var(--accent)]/5',
          )}
        >
          <p className="text-xs text-[var(--fg-muted)]">
            Modo edição: arraste para mover, alças para redimensionar.
            <span className="ml-2 opacity-60">Esc cancela · Ctrl+S salva</span>
          </p>
          <div className="flex items-center gap-2">
            <AddWidgetMenu
              pageId="financial"
              presentIds={presentIds}
              onAdd={handleAdd}
            />
            <button
              type="button"
              onClick={cancelEdit}
              className={cn(
                'rounded-md border px-3 py-1.5 text-xs font-medium transition',
                'border-[var(--border)] bg-[var(--surface)] text-[var(--fg-muted)] hover:text-[var(--fg)]',
              )}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={saveEdit}
              disabled={saveMutation.isPending}
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-medium transition',
                'bg-[var(--accent)] text-[var(--accent-fg)] hover:opacity-90',
                saveMutation.isPending && 'opacity-60',
              )}
            >
              {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      )}

      {saveMutation.isError && (
        <p className="text-xs text-red-500">
          Falha ao salvar o layout. Tente novamente.
        </p>
      )}

      <DashboardGrid
        pageId="financial"
        layout={activeLayout}
        mode={mode}
        onLayoutChange={handleLayoutChange}
        onRemove={handleRemove}
      />

      <div className="border-t border-[var(--border)]" />

      {/* Lista de lancamentos (fixa, fora do grid) */}
      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold text-[var(--fg)]">Lançamentos</h2>

        {/* Totais do filtro */}
        <div data-tour="financeiro-totais" className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] px-4 py-3 shadow-[var(--shadow-card)]">
            <p className="text-xs text-[var(--fg-muted)]">Receita</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-emerald-600">
              {totalsQ.isLoading ? '—' : formatBrl(totals?.totalIncome ?? 0)}
            </p>
          </div>
          <div className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] px-4 py-3 shadow-[var(--shadow-card)]">
            <p className="text-xs text-[var(--fg-muted)]">Despesa</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-red-600">
              {totalsQ.isLoading ? '—' : formatBrl(totals?.totalExpense ?? 0)}
            </p>
          </div>
          <div className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] px-4 py-3 shadow-[var(--shadow-card)]">
            <p className="text-xs text-[var(--fg-muted)]">Saldo</p>
            <p
              className={cn(
                'mt-1 text-lg font-semibold tabular-nums',
                totalsQ.isLoading
                  ? 'text-[var(--fg-muted)]'
                  : (totals?.balance ?? 0) >= 0
                    ? 'text-emerald-600'
                    : 'text-red-600',
              )}
            >
              {totalsQ.isLoading ? '—' : formatBrl(totals?.balance ?? 0)}
            </p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            data-tour="financeiro-busca"
            placeholder="Buscar por descrição, anotação ou cliente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="min-w-[220px] flex-1 rounded border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--fg)]"
          />
          <div data-tour="financeiro-filtros" className="flex flex-wrap items-center gap-2">
          <GlassSelect
            value={filters.type ?? ''}
            onChange={(v) =>
              setFilters((f) => ({
                ...f,
                type: (v || undefined) as FinancialTransactionType | undefined,
                page: 1,
              }))
            }
            options={[
              { value: '', label: 'Todos tipos' },
              ...Object.entries(TYPE_LABEL).map(([k, v]) => ({ value: k, label: v })),
            ]}
            className="w-40"
            aria-label="Tipo de lançamento"
          />
          <GlassSelect
            value={filters.status ?? ''}
            onChange={(v) =>
              setFilters((f) => ({
                ...f,
                status: (v || undefined) as FinancialTransactionStatus | undefined,
                page: 1,
              }))
            }
            options={[
              { value: '', label: 'Todos status' },
              ...Object.entries(STATUS_LABEL).map(([k, v]) => ({ value: k, label: v })),
            ]}
            className="w-40"
            aria-label="Status do lançamento"
          />
          <GlassSelect
            value={filters.accountId ?? ''}
            onChange={(v) =>
              setFilters((f) => ({
                ...f,
                accountId: v || undefined,
                page: 1,
              }))
            }
            options={[
              { value: '', label: 'Todas contas' },
              ...(accountsQ.data ?? []).map((a) => ({ value: a.id, label: a.name })),
            ]}
            className="w-44"
            aria-label="Conta bancária"
          />
          <CategoryFilter
            value={filters.categoryIds ?? []}
            onChange={(ids) =>
              setFilters((f) => ({
                ...f,
                categoryIds: ids.length > 0 ? ids : undefined,
                page: 1,
              }))
            }
            className="w-44"
            aria-label="Categoria"
          />
          </div>

          {/* Filtros de data */}
          <div data-tour="financeiro-periodo" className="flex flex-wrap items-center gap-2">
          <GlassSelect
            value={dateDimension}
            onChange={handleDimensionChange}
            options={DATE_DIMENSION_OPTIONS as unknown as { value: string; label: string }[]}
            className="w-36"
            aria-label="Dimensão da data"
          />
          <GlassDateRangePicker
            from={dateFrom}
            to={dateTo}
            onChange={handleDateRangeChange}
            className="w-56"
            aria-label="Filtro de período"
          />

          <button
            type="button"
            onClick={handleClearFilters}
            disabled={!hasActiveFilters}
            className={cn(
              'inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-xs font-medium transition',
              'border-[var(--border)] bg-[var(--surface)] text-[var(--fg-muted)]',
              hasActiveFilters
                ? 'hover:text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]'
                : 'cursor-not-allowed opacity-50',
            )}
            aria-label="Limpar filtros"
            title="Limpar filtros"
          >
            <FilterX className="h-3.5 w-3.5" />
            Limpar filtros
          </button>
          </div>
        </div>

        <div data-tour="financeiro-tabela" className="overflow-hidden rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] shadow-[var(--shadow-card)]">
          <table className="w-full text-sm">
            <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-xs uppercase text-[var(--fg-muted)]">
              <tr>
                <SortableTh
                  className="w-28 px-3 py-2 text-left"
                  field="dueDate"
                  currentOrderBy={filters.orderBy}
                  currentOrderDir={filters.orderDir}
                  onSort={handleSort}
                >
                  Vencimento
                </SortableTh>
                <SortableTh
                  className="w-28 px-3 py-2 text-left"
                  field="paymentDate"
                  currentOrderBy={filters.orderBy}
                  currentOrderDir={filters.orderDir}
                  onSort={handleSort}
                >
                  Pagamento
                </SortableTh>
                <th className="px-3 py-2 text-left">Lançamento</th>
                <SortableTh
                  className="w-40 px-3 py-2 text-left"
                  field="category"
                  currentOrderBy={filters.orderBy}
                  currentOrderDir={filters.orderDir}
                  onSort={handleSort}
                >
                  Categoria
                </SortableTh>
                <th className="w-36 px-3 py-2 text-left">Conta</th>
                <SortableTh
                  className="w-32 px-3 py-2 text-right"
                  field="amount"
                  currentOrderBy={filters.orderBy}
                  currentOrderDir={filters.orderDir}
                  onSort={handleSort}
                  align="right"
                >
                  Valor
                </SortableTh>
                <th className="w-24 px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {listQ.isLoading && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-6 text-center text-[var(--fg-muted)]"
                  >
                    Carregando...
                  </td>
                </tr>
              )}
              {!listQ.isLoading && items.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-8 text-center text-[var(--fg-muted)]"
                  >
                    Nenhum lançamento encontrado.
                  </td>
                </tr>
              )}
              {items.map((t) => (
                <TxRow
                  key={t.id}
                  tx={t}
                  onMarkPaid={(id) => markPaidMut.mutate(id)}
                  onMarkUnpaid={(id) => markUnpaidMut.mutate(id)}
                  onEditPaymentDate={async (id, isoDate) => {
                    await updatePaymentDateMut.mutateAsync({ id, paymentDate: isoDate });
                  }}
                  onRequestDelete={setDeleteTarget}
                />
              ))}
            </tbody>
            {items.length > 0 && (
              <tfoot className="border-t border-[var(--card-border)] bg-[color-mix(in_oklch,var(--fg)_3%,transparent)] text-xs">
                <tr>
                  <td colSpan={7} className="px-3 py-2 text-[var(--fg-muted)]">
                    Subtotal desta página: {items.length} lançamento(s)
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Rodapé de paginação */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-1 py-1">
          {/* Contador */}
          <span className="text-xs text-[var(--fg-muted)]">
            {totalItems === 0
              ? 'Nenhum lançamento'
              : `Mostrando ${firstItem}–${lastItem} de ${totalItems}`}
          </span>

          {/* Controles centrais */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => goToPage(1)}
              className={cn(
                'inline-flex h-7 w-7 items-center justify-center rounded border text-xs transition',
                'border-[var(--border)] bg-[var(--surface)] text-[var(--fg-muted)]',
                'hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
                'disabled:cursor-not-allowed disabled:opacity-40',
              )}
              title="Primeira página"
            >
              <ChevronsLeft className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => goToPage(currentPage - 1)}
              className={cn(
                'inline-flex h-7 w-7 items-center justify-center rounded border text-xs transition',
                'border-[var(--border)] bg-[var(--surface)] text-[var(--fg-muted)]',
                'hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
                'disabled:cursor-not-allowed disabled:opacity-40',
              )}
              title="Página anterior"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>

            {/* Input direto de página */}
            <div className="flex items-center gap-1 text-xs text-[var(--fg-muted)]">
              <span>Página</span>
              <input
                ref={pageInputRef}
                type="number"
                min={1}
                max={totalPages}
                defaultValue={currentPage}
                key={currentPage}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const v = parseInt((e.target as HTMLInputElement).value, 10);
                    if (!Number.isNaN(v)) goToPage(v);
                  }
                }}
                onBlur={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!Number.isNaN(v)) goToPage(v);
                }}
                className={cn(
                  'w-12 rounded border border-[var(--border)] bg-[var(--surface)] px-1 py-0.5',
                  'text-center text-xs text-[var(--fg)]',
                  '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                )}
              />
              <span>de {totalPages}</span>
            </div>

            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => goToPage(currentPage + 1)}
              className={cn(
                'inline-flex h-7 w-7 items-center justify-center rounded border text-xs transition',
                'border-[var(--border)] bg-[var(--surface)] text-[var(--fg-muted)]',
                'hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
                'disabled:cursor-not-allowed disabled:opacity-40',
              )}
              title="Próxima página"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => goToPage(totalPages)}
              className={cn(
                'inline-flex h-7 w-7 items-center justify-center rounded border text-xs transition',
                'border-[var(--border)] bg-[var(--surface)] text-[var(--fg-muted)]',
                'hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
                'disabled:cursor-not-allowed disabled:opacity-40',
              )}
              title="Última página"
            >
              <ChevronsRight className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Seletor de page size */}
          <div className="flex items-center gap-1.5 text-xs text-[var(--fg-muted)]">
            <span>Por página:</span>
            <GlassSelect
              value={String(currentPageSize)}
              onChange={(v) => changePageSize(Number(v))}
              options={PAGE_SIZE_OPTIONS.map((n) => ({
                value: String(n),
                label: String(n),
              }))}
              className="w-20"
              aria-label="Itens por página"
            />
          </div>
        </div>
      </section>

      <DeleteTransactionDialog
        tx={deleteTarget}
        onClose={() => {
          if (deleteSimpleMut.isPending || deleteBatchMut.isPending) return;
          setDeleteTarget(null);
          deleteSimpleMut.reset();
          deleteBatchMut.reset();
        }}
        onConfirmSimple={(id) => deleteSimpleMut.mutate(id)}
        onConfirmBatch={(id, scope) => deleteBatchMut.mutate({ id, scope })}
        isPending={deleteSimpleMut.isPending || deleteBatchMut.isPending}
        error={(deleteSimpleMut.error ?? deleteBatchMut.error) as ApiError | null}
      />
    </div>
  );
}

function TxRow({
  tx,
  onMarkPaid,
  onMarkUnpaid,
  onEditPaymentDate,
  onRequestDelete,
}: {
  tx: FinancialTransactionListItem;
  onMarkPaid: (id: string) => void;
  onMarkUnpaid: (id: string) => void;
  onEditPaymentDate: (id: string, isoDate: string) => Promise<void>;
  onRequestDelete: (tx: FinancialTransactionListItem) => void;
}) {
  const amount = Number(tx.amount ?? 0);
  const amountColor =
    tx.type === 'income'
      ? 'text-emerald-600'
      : tx.type === 'expense'
        ? 'text-red-600'
        : 'text-[var(--fg)]';
  const sign = tx.type === 'expense' ? '-' : tx.type === 'income' ? '+' : '';
  const overdueClass = tx.status === 'overdue' ? 'text-red-600 font-medium' : '';
  const paidClass =
    tx.status === 'paid' ? 'text-[var(--fg-muted)]' : '';

  return (
    <tr className="border-t border-[var(--card-border)] hover:bg-[color-mix(in_oklch,var(--fg)_3%,transparent)]">
      <td className={cn('px-3 py-2 tabular-nums', overdueClass, paidClass)}>
        {formatDate(tx.dueDate)}
      </td>
      <PaymentDateCell tx={tx} onEditPaymentDate={onEditPaymentDate} />
      <td className="px-3 py-2">
        <Link
          to={`/escritorio/financeiro/${tx.id}`}
          className={cn('text-[var(--fg)] hover:underline', paidClass)}
        >
          {tx.description}
        </Link>
        <div className="mt-1 flex flex-wrap gap-1 text-xs text-[var(--fg-muted)]">
          {tx.client && (
            <span className="inline-flex items-center rounded bg-blue-500/15 px-1.5 py-0.5 text-blue-600">
              {tx.client.name}
            </span>
          )}
          {tx.case && (
            <span className="inline-flex items-center rounded bg-purple-500/15 px-1.5 py-0.5 text-purple-600">
              {tx.case.numeroCnj ?? 'Processo'}
            </span>
          )}
          {tx.contractId && (
            <span className="inline-flex items-center rounded bg-amber-500/15 px-1.5 py-0.5 text-amber-600">
              Parcela
              {tx.contractInstallmentSeq ? ` ${tx.contractInstallmentSeq}` : ''}
            </span>
          )}
          {tx.isInternalOnly && (
            <span className="inline-flex items-center rounded bg-[var(--surface-muted)] px-1.5 py-0.5 text-[var(--fg-muted)]">
              Interno
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-[var(--fg-muted)]">
        {tx.category ? (
          <span
            className="inline-flex items-center gap-1"
            style={{ color: tx.category.color }}
          >
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: tx.category.color }}
            />
            {tx.category.name}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="px-3 py-2 text-[var(--fg-muted)]">
        {tx.account?.name ?? '—'}
      </td>
      <td
        className={cn(
          'px-3 py-2 text-right font-medium tabular-nums',
          amountColor,
        )}
      >
        {sign}
        {formatBrlString(amount)}
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-end gap-1.5">
          {tx.status === 'paid' ? (
            <button
              type="button"
              onClick={() => onMarkUnpaid(tx.id)}
              className="text-[10px] uppercase tracking-wide text-[var(--fg-muted)] hover:text-[var(--fg)] cursor-pointer"
              title={tx.type === 'income' ? 'Clique para desmarcar recebimento' : 'Clique para desmarcar pagamento'}
            >
              {tx.type === 'income' ? 'Recebido' : 'Pago'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onMarkPaid(tx.id)}
              className="inline-flex items-center gap-1 rounded bg-emerald-600 px-2 py-1 text-xs text-white hover:opacity-90"
              title={tx.type === 'income' ? 'Marcar como recebido' : 'Marcar como pago'}
            >
              <Check className="h-3 w-3" />
              {tx.type === 'income' ? 'Receber' : 'Pagar'}
            </button>
          )}
          <button
            type="button"
            onClick={() => onRequestDelete(tx)}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-[var(--fg-muted)] transition hover:bg-red-500/10 hover:text-red-600"
            title="Excluir lançamento"
            aria-label="Excluir lançamento"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

type DeleteScopeChoice = 'only' | FinancialTransactionDeleteScope;

/**
 * Diálogo de exclusão a partir da lixeira na lista (sem precisar abrir o
 * lançamento). Dois modos:
 *  - sem vínculo (contractId nulo): confirmação simples, sem round-trip.
 *  - com vínculo (parcela/recorrência — mesmo mecanismo `contractId` +
 *    `contractInstallmentSeq`): busca o preview de contagens e oferece as
 *    3 opções de escopo, com contagem e aviso de já-pagos antes de excluir.
 */
function DeleteTransactionDialog({
  tx,
  onClose,
  onConfirmSimple,
  onConfirmBatch,
  isPending,
  error,
}: {
  tx: FinancialTransactionListItem | null;
  onClose: () => void;
  onConfirmSimple: (id: string) => void;
  onConfirmBatch: (id: string, scope: FinancialTransactionDeleteScope) => void;
  isPending: boolean;
  error: ApiError | null;
}) {
  const [scope, setScope] = useState<DeleteScopeChoice>('only');
  const hasContract = !!tx?.contractId;

  // Reseta a escolha sempre que o dialogo abre para um lancamento diferente.
  useEffect(() => {
    setScope('only');
  }, [tx?.id]);

  const previewQ = useQuery({
    queryKey: ['financial', 'tx', 'delete-preview', tx?.id],
    queryFn: () => apiClient.financialTransactionsDeletePreview(tx!.id),
    enabled: !!tx && hasContract,
    staleTime: 0,
  });

  useEffect(() => {
    if (!tx) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [tx, onClose]);

  if (!tx) return null;

  function handleConfirm() {
    if (!tx) return;
    if (!hasContract || scope === 'only') {
      onConfirmSimple(tx.id);
    } else {
      onConfirmBatch(tx.id, scope);
    }
  }

  // Sem vinculo: sempre pode confirmar. Com vinculo: so libera depois do
  // preview carregar com sucesso — se falhou, forca fechar/tentar de novo
  // em vez de deixar excluir "as cegas" sem o usuario ver as 3 opcoes.
  const canConfirm = !isPending && (!hasContract || previewQ.isSuccess);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-tx-modal-title"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={isPending ? undefined : onClose}
        aria-hidden
      />
      <div
        className={cn(
          'relative z-[61] flex w-full max-w-md flex-col overflow-hidden rounded-lg border shadow-xl',
          'border-[var(--modal-border,var(--card-border))] bg-[var(--modal-bg,var(--card-bg))] backdrop-blur-[var(--blur)]',
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-[var(--border)] px-5 py-4">
          <div>
            <h2 id="delete-tx-modal-title" className="text-base font-semibold text-[var(--fg)]">
              Excluir lançamento
            </h2>
            <p className="mt-1 text-xs text-[var(--fg-muted)]">
              {tx.description} · {formatBrlString(tx.amount)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            aria-label="Fechar"
            className="rounded-md p-1 text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)] disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex flex-col gap-3 px-5 py-4">
          {!hasContract && (
            <p className="text-sm text-[var(--fg)]">
              Tem certeza que deseja excluir este lançamento? Essa ação não pode ser desfeita.
            </p>
          )}

          {hasContract && previewQ.isLoading && (
            <p className="text-sm text-[var(--fg-muted)]">Carregando opções...</p>
          )}

          {hasContract && previewQ.isError && (
            <p className="text-sm text-red-500">
              Não foi possível carregar as opções de exclusão. Tente novamente.
            </p>
          )}

          {hasContract && previewQ.data && (
            <>
              <p className="text-sm text-[var(--fg)]">
                Este lançamento faz parte de uma série (parcela ou recorrência). O que deseja excluir?
              </p>
              <div className="flex flex-col gap-2">
                <DeleteScopeOption
                  id="scope-only"
                  checked={scope === 'only'}
                  onSelect={() => setScope('only')}
                  label="Apenas este"
                  counts={previewQ.data.onlyThis}
                />
                <DeleteScopeOption
                  id="scope-next"
                  checked={scope === 'this_and_next'}
                  onSelect={() => setScope('this_and_next')}
                  label="Este e os próximos"
                  counts={previewQ.data.thisAndNext}
                />
                <DeleteScopeOption
                  id="scope-all"
                  checked={scope === 'all'}
                  onSelect={() => setScope('all')}
                  label="Todos da série"
                  counts={previewQ.data.all}
                />
              </div>
            </>
          )}

          {error && (
            <p className="text-xs text-red-500" role="alert">
              {error.message || 'Falha ao excluir. Tente novamente.'}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-[var(--border)] bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--fg)] hover:bg-[var(--surface-muted)] disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {isPending ? 'Excluindo...' : 'Excluir'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function DeleteScopeOption({
  id,
  checked,
  onSelect,
  label,
  counts,
}: {
  id: string;
  checked: boolean;
  onSelect: () => void;
  label: string;
  counts: { count: number; paidCount: number };
}) {
  return (
    <label
      htmlFor={id}
      className={cn(
        'flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2 transition',
        checked
          ? 'border-[var(--accent)] bg-[var(--accent)]/5'
          : 'border-[var(--border)] hover:bg-[color-mix(in_oklch,var(--fg)_4%,transparent)]',
      )}
    >
      <input
        id={id}
        type="radio"
        name="delete-scope"
        checked={checked}
        onChange={onSelect}
        className="mt-0.5 h-3.5 w-3.5 accent-[var(--accent)]"
      />
      <span className="flex flex-col gap-0.5">
        <span className="text-sm text-[var(--fg)]">
          {label} <span className="text-[var(--fg-muted)]">({counts.count})</span>
        </span>
        {counts.paidCount > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] text-amber-600">
            <AlertTriangle className="h-3 w-3" />
            {counts.paidCount} já {counts.paidCount === 1 ? 'está pago' : 'estão pagos'}
          </span>
        )}
      </span>
    </label>
  );
}
